import {
  AllCommunityModule,
  type CellClassParams,
  type CellClickedEvent,
  ClientSideRowModelModule,
  enableDevValidations,
  type ColDef,
  type ColGroupDef,
  type ColumnRowGroupChangedEvent,
  type DragStoppedEvent,
  type NewColumnsLoadedEvent,
  type DefaultMenuItem,
  type FilterModel,
  type GetContextMenuItemsParams,
  type GetMainMenuItems,
  type ExcelStyle,
  type GetMainMenuItemsParams,
  type GetRowIdFunc,
  type GetRowIdParams,
  type GridApi,
  type IAggFuncs,
  type GridOptions,
  type GridPreDestroyedEvent,
  type GridSizeChangedEvent,
  type GridState,
  type IDetailCellRendererParams,
  type INoRowsOverlayComp,
  type INoRowsOverlayParams,
  type IRowNode,
  type IsRowFilterable,
  type IsRowPinned,
  type MenuItemDef,
  type ModelUpdatedEvent,
  ModuleRegistry,
  type PaginationNumberFormatterParams,
  type RowDragEndEvent,
  type RowGroupingDisplayType,
  type RowSelectionOptions,
  type SelectionChangedEvent,
  type SideBarDef,
  type GridReadyEvent,
  type SortChangedEvent,
} from "ag-grid-community"
import {
  AllEnterpriseModule,
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  MenuModule,
  RowGroupingModule,
  SetFilterModule,
} from "ag-grid-enterprise"
import { AgGridReact } from "ag-grid-react"
import { useFlags } from "~/features/feature-flags"
import { uniq } from "~/utils/tsUtils"
import { debounce, flatten } from "lodash"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { flushSync } from "react-dom"
import { createRoot } from "react-dom/client"
import { Gear } from "@phosphor-icons/react"
import { getTailwindValue } from "~/utils/tailwind"
import { cn } from "~/utils/helpers"
import { createCustomTheme } from "~/components/reusable/agGridTheme"
import { useTheme } from "next-themes"
import { useSafeSessionStorage } from "~/hooks/useSafeLocalStorage"
import { z } from "zod"
import { CustomPagination, type PageSizeOption } from "~/components/reusable/CustomPagination"
import { Card } from "~/components/reusable/Card"
import {
  useSelectedVariantHandlers,
  SelectedVariantsProvider,
  useSelectedVariantHandlersOptional,
} from "~/context/SelectedVariantsProvider"
import {
  debouncedResetIsApplyingView,
  dropStaleFilterModelEntries,
  restoreColDefAggFuncs,
  type AggregationRestore,
  type SavedStateMerge,
  type SavedStateMigration,
  stripTransientGridState,
  useGridStateStorage,
} from "~/hooks/useGridState"
import LoadingOverlay from "~/components/reusable/LoadingOverlay"
import {
  composeCellStyle,
  deriveInspectionColumnState,
  type Formula,
  FormulaBar,
  getInspectionCellStyle,
  type InspectedCell,
  relationalFormulaContext,
  type RelationalFormulaNoteFn,
  useDismissOnOutsideClick,
} from "~/components/reusable/table/relationalFormula"
import { useExportConfig } from "~/hooks/useExportConfig"
import { useConfigureExportDialogStore } from "~/stores/configure-export-dialog-store"
import {
  generateWeightedAverageFromParams,
  isColGroupDef,
  makeProcessExportCell,
  mapColDefLeaves,
  pinnedToInitial,
} from "~/utils/ag-utils"
import { createAutoPagination, measureAutoPageSize } from "~/utils/autoPagination"
import { EXCEL_BUILTIN_SHORT_DATE, numberExcelStyles } from "~/utils/excelStyles"

ModuleRegistry.registerModules([
  AllCommunityModule,
  AllEnterpriseModule,
  ClientSideRowModelModule,
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  MenuModule,
  RowGroupingModule,
  SetFilterModule,
])

// Grid misconfiguration warnings are opt-in: ValidationModule is not part of the All*
// bundles, so without this dev builds get no AG Grid diagnostics at all.
if (import.meta.env.DEV) {
  enableDevValidations()
}

export type SortColumn = {
  name: string | number | undefined
  direction: string | null
}

let gearIconElement: HTMLElement | undefined
function getGearIconElement(): HTMLElement {
  if (!gearIconElement) {
    const host = document.createElement("span")
    const root = createRoot(host)
    flushSync(() => root.render(<Gear size={16} />))
    gearIconElement = host
  }
  return gearIconElement
}

// Cell classes set by the column templates; ag-grid maps them to xlsx cell types.
const excelStylesFor = (decimals: number): ExcelStyle[] => [
  { id: "dateType", dataType: "DateTime", numberFormat: { format: EXCEL_BUILTIN_SHORT_DATE } },
  { id: "idType", dataType: "String" },
  ...numberExcelStyles(decimals),
]

// Firefox on Linux/Mac reports scrollbarWidth=0 (overlay scrollbars), causing ag-grid to
// render the horizontal scroll viewport at 0px tall — invisible. Enforce a visible minimum.
let _cachedScrollbarWidth: number | undefined
function getGridScrollbarWidth(): number {
  if (_cachedScrollbarWidth !== undefined) return _cachedScrollbarWidth
  const div = document.createElement("div")
  div.style.cssText = "overflow:scroll;position:absolute;visibility:hidden;top:-9999px"
  document.body.appendChild(div)
  _cachedScrollbarWidth = Math.max(div.offsetWidth - div.clientWidth, 8)
  document.body.removeChild(div)
  return _cachedScrollbarWidth
}

const pageSizeSchema = z.union([
  z.literal("auto"),
  z.literal(25),
  z.literal(50),
  z.literal(100),
  z.literal(250),
])

export const useDataTablePageSize = (id: string, defaultPageSize: PageSizeOption = "auto") => {
  return useSafeSessionStorage<PageSizeOption>(
    `${id}-pageSize`,
    defaultPageSize,
    pageSizeSchema,
    false,
    false,
  )
}

const TableLoadingOverlay = () => <LoadingOverlay messages="Loading..." />

type NoRowsOverlayParams = INoRowsOverlayParams & { noRowsMessage: string }

export class NoRowsOverlay implements INoRowsOverlayComp {
  eGui!: HTMLElement

  // AG Grid invokes the overlay lifecycle; nothing in our code calls these directly.
  // fallow-ignore-next-line unused-class-member
  init(params: NoRowsOverlayParams) {
    this.eGui = document.createElement("div")
    this.refresh(params)
  }

  // fallow-ignore-next-line unused-class-member
  getGui() {
    return this.eGui
  }

  refresh(params: NoRowsOverlayParams): void {
    this.eGui.innerHTML = `
    <span class="text-lg"> ${params.noRowsMessage} </span>
    `
  }
}

// See if a SelectedVariantsProvider exist above the dataTableV2
export function DataTableBaseV2<T>(props: DataTableBaseProps<T>) {
  const existing = useSelectedVariantHandlersOptional()

  if (existing) {
    return <DataTableBaseV2Inner<T> {...props} />
  }

  return (
    <SelectedVariantsProvider<any>
      gridRef={props.gridRef as React.RefObject<AgGridReact<any> | null>}
    >
      <DataTableBaseV2Inner<T> {...props} />
    </SelectedVariantsProvider>
  )
}

interface DataTableBaseProps<T> {
  id: string
  gridRef: React.RefObject<AgGridReact<T> | null>
  /** Omitted when `gridOptions.rowModelType` is "serverSide": that model loads its own rows. */
  rowData?: T[]
  rowIdField?: string
  gridOptions?: GridOptions<T>
  rowSelection?: RowSelectionOptions | "single" | "multiple"
  showAggregationHeader?: boolean
  columnDefs: (ColDef<T> | ColGroupDef<T>)[]
  defaultColumnDef?: ColDef
  /** When true, saved column widths from previous sessions are ignored so flex columns always fill available space. */
  disableColumnSizingPersistence?: boolean
  startOnFirstPage?: boolean
  /**
   * When true, the grid neither restores nor saves its state (column order, widths, sort, filters),
   * so every mount starts from `columnDefs`. For tables whose columns are derived from their data.
   */
  disableStatePersistence?: boolean
  /** Rewrites a saved or view state against the registered columns before the grid applies it. */
  migrateSavedState?: SavedStateMigration
  /** Rewrites a state about to be saved, given the state saved before it. */
  mergeSavedState?: SavedStateMerge
  /** Re-activates the aggregation a full state apply dropped. Defaults to `restoreColDefAggFuncs`. */
  restoreAggregation?: AggregationRestore
  /** Named aggFuncs the columns may use, beside the built-in `unique` and `weightedAverage`. */
  aggFuncs?: IAggFuncs<T>
  initialSortColumn?: SortColumn
  /** Default filter model to apply when the grid initializes (if no user filter is set) */
  defaultFilterModel?: FilterModel
  /** Rows pinned to the top of the grid. Reactive — updates apply without a remount. */
  pinnedTopRowData?: T[]
  /** Quick filter text applied across all columns. Reactive — updates apply without a remount. */
  quickFilterText?: string
  onSelectionChanged?: (event: SelectionChangedEvent) => void
  autoGroupColumnDef?: ColDef<T>
  emptyMessage?: string
  getMainMenuItems?: GetMainMenuItems<T, any> | undefined
  grandTotalRow?: "top" | "bottom" | "pinnedBottom"
  loading?: boolean
  sideBar?: SideBarDef | string | string[] | boolean | null
  groupAggFiltering?: boolean | IsRowFilterable
  /**
   * Trade-off to pick per table. When truthy, AG Grid force-enables `paginateChildRows`,
   * so on paginated tables an expanded group's children split across pages. Setting
   * `false` keeps children on one page but renders single-child groups as an extra parent
   * row above their only child — avoid it for tables whose leaf group always wraps a
   * single row.
   */
  groupHideParentOfSingleChild?: boolean | "leafGroupsOnly"
  groupDisplayType?: RowGroupingDisplayType
  masterDetail?: boolean | undefined
  detailCellRendererParams?: IDetailCellRendererParams<T, any> | undefined
  detailCellRenderer?: React.FC<IDetailCellRendererParams<T, any>> | undefined
  detailRowAutoHeight?: boolean
  /** With `detailRowAutoHeight`, caps the detail row at this px (scrolls past it). See the
   * `.ag-details-grid-auto-height` rule in globals.css. */
  maxDetailRowHeight?: number
  keepDetailRows?: boolean
  autoHeight?: boolean
  minHeight?: number
  enableRowPinning?: boolean
  onRowDragEnd?: (event: RowDragEndEvent<T>) => void
  rowDragMultiRow?: boolean
  isRowPinned?: IsRowPinned<T>
  showPagination?: boolean
  /** Page size until the user picks one. "auto" fits the rows to the grid height. */
  defaultPageSize?: PageSizeOption
  onCellValueChanged?: (params: any) => void
  onCellClicked?: (event: CellClickedEvent<T>) => void
  onGridReady?: (event: GridReadyEvent<T, any>) => void
  onDragStopped?: (event: DragStoppedEvent<T>) => void
  /**
   * Fires on every columnDefs change from the grid's creation on: events from before the grid is
   * ready are replayed after `onGridReady`.
   */
  onNewColumnsLoaded?: (event: NewColumnsLoadedEvent<T>) => void
  onSortChanged?: (event: SortChangedEvent) => void
  onFilterChanged?: (event: any) => void
  columnBorder?: boolean
  /** Dense rows + smaller type for spreadsheet-grade tables (e.g. allocation lines). */
  compact?: boolean
  enableCellTextSelection?: boolean
  context?: Record<string, unknown>
  getContextMenuItems?: (
    params: GetContextMenuItemsParams<T>,
  ) => (DefaultMenuItem | MenuItemDef<T>)[]
  /**
   * Scalar values referenced by `param` nodes in any colDef's `context.relationalFormula`
   * (e.g. `{ weeksInPeriod: 12 }`). Filter-derived numbers that aren't part of row data.
   */
  formulaParams?: Record<string, number>
  /** Display labels for `formulaParams` keys, used in the FormulaBar's chip tooltips. */
  formulaParamLabels?: Record<string, string>
  cardClassName?: string
  footerClassName?: string
  /** Rendered inside the card, under the grid. */
  cardFooter?: React.ReactNode
}

function DataTableBaseV2Inner<T>({
  id,
  gridRef,
  rowData,
  rowIdField = "id",
  gridOptions,
  columnDefs,
  isRowPinned,
  defaultColumnDef,
  rowSelection,
  onSelectionChanged,
  initialSortColumn,
  defaultFilterModel,
  pinnedTopRowData,
  quickFilterText,
  autoGroupColumnDef,
  emptyMessage,
  getMainMenuItems,
  grandTotalRow,
  loading,
  sideBar,
  groupAggFiltering,
  groupHideParentOfSingleChild = "leafGroupsOnly",
  groupDisplayType,
  masterDetail,
  detailCellRendererParams,
  detailCellRenderer,
  detailRowAutoHeight = false,
  maxDetailRowHeight,
  keepDetailRows = true,
  autoHeight,
  enableRowPinning = false,
  onRowDragEnd,
  rowDragMultiRow = false,
  onCellValueChanged,
  onCellClicked,
  onGridReady,
  showPagination = true,
  defaultPageSize,
  minHeight = 200,
  onDragStopped,
  onNewColumnsLoaded,
  onSortChanged,
  onFilterChanged,
  columnBorder = true,
  compact = false,
  enableCellTextSelection = true,
  context,
  getContextMenuItems: getContextMenuItemsProp,
  disableColumnSizingPersistence = false,
  startOnFirstPage = false,
  disableStatePersistence = false,
  migrateSavedState,
  mergeSavedState,
  restoreAggregation = restoreColDefAggFuncs,
  aggFuncs: extraAggFuncs,
  formulaParams,
  formulaParamLabels,
  cardClassName,
  footerClassName,
  cardFooter,
}: DataTableBaseProps<T>) {
  const { resolvedTheme } = useTheme()

  // ── Relational-formula inspection ────────────────────────────────────────
  // Feature is dormant unless at least one colDef carries `context.relationalFormula`.
  const { formulasByColId, noteFnsByColId, leafDefsByColId } = useMemo(() => {
    const formulas = new Map<string, Formula>()
    const noteFns = new Map<string, RelationalFormulaNoteFn>()
    const leafDefs = new Map<string, ColDef<T>>()
    const walk = (defs: (ColDef<T> | ColGroupDef<T>)[]) => {
      for (const def of defs) {
        if (isColGroupDef(def)) {
          walk(def.children)
          continue
        }
        const colId = def.colId ?? def.field
        if (!colId) continue
        leafDefs.set(colId, def)
        const relational = relationalFormulaContext(def)
        if (relational?.relationalFormula) formulas.set(colId, relational.relationalFormula)
        if (relational?.relationalFormulaNote) noteFns.set(colId, relational.relationalFormulaNote)
      }
    }
    walk(columnDefs)
    return {
      formulasByColId: formulas,
      noteFnsByColId: noteFns,
      leafDefsByColId: leafDefs,
    }
  }, [columnDefs])
  // Gated by LaunchDarkly `columnRelations` flag — when off, the feature stays
  // fully dormant: no cellStyle decoration, no click handling, no FormulaBar.
  const { columnRelations: columnRelationsFlag } = useFlags()
  const inspectionEnabled = !!columnRelationsFlag && formulasByColId.size > 0

  const [inspectedCell, setInspectedCell] = useState<InspectedCell | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  // Snapshots captured on inspection enter, used to restore order/visibility on exit.
  const baselineOrderRef = useRef<string[] | null>(null)
  const baselineHiddenRef = useRef<Set<string> | null>(null)
  // True from inspection-apply through inspection-restore. Save callbacks skip
  // persisting while this is set so the transient column state never reaches
  // localStorage (see `debouncedSaveState` and `onGridPreDestroyed`).
  const inspectionActiveRef = useRef(false)

  const { saveState, getCurrentState, applyState, setGridApi, getGridApi, getInitialState } =
    useGridStateStorage(id, { migrateSavedState, restoreAggregation, mergeSavedState })
  const [selectedPageSize, setSelectedPageSize] = useDataTablePageSize(id, defaultPageSize)

  // Get saved state only once on mount - don't recalculate on every render
  // This prevents the key prop from changing and causing full grid remounts
  // Using useState with lazy initializer to capture the value once
  const [initialState] = useState(() => {
    if (disableStatePersistence) return undefined
    const saved = getInitialState(leafDefsByColId)
    const state = startOnFirstPage && saved ? { ...saved, pagination: undefined } : saved
    if (disableColumnSizingPersistence) {
      const rest = Object.fromEntries(
        Object.entries(state ?? {}).filter(([key]) => key !== "columnSizing"),
      ) as typeof state
      return rest && Object.keys(rest).length > 0 ? rest : undefined
    }
    return state
  })
  // The columns AG Grid applies initialState to: those it is created with.
  const [initialColIds] = useState(() => new Set(leafDefsByColId.keys()))
  const debouncedSaveState = useMemo(
    () =>
      // eslint-disable-next-line react-hooks/refs -- debounce body runs async (not during render); the ref read is safe.
      debounce(() => {
        // Skip saves while inspecting — the temporary column splice/visibility
        // would otherwise be written to localStorage and leak across sessions.
        if (inspectionActiveRef.current) return
        if (!disableStatePersistence) saveState()
        debouncedResetIsApplyingView(id)
      }, 500),
    [saveState, id, disableStatePersistence],
  )

  const customTheme = useMemo(
    () => createCustomTheme(columnBorder, compact),
    [columnBorder, compact],
  )

  useEffect(() => {
    document.body.dataset.agThemeMode = resolvedTheme === "dark" ? "dark" : "light"
  }, [resolvedTheme])

  const selectionColumnDef = useMemo<ColDef>(() => {
    return {
      suppressHeaderMenuButton: true,
      pinned: "left",
      minWidth: 52,
      maxWidth: 52,
    }
  }, [])

  const aggFuncs = useMemo((): IAggFuncs<T> => {
    return {
      ...extraAggFuncs,
      unique: (params: any) => uniq(flatten(params.values)),
      // Named so it survives column-state serialization. Reads value key from
      // colDef.field and weight key from colDef.context.weightKey.
      weightedAverage: (params: any) => {
        const valueKey: string | undefined = params.colDef?.field
        const weightKey: string | undefined = params.colDef?.context?.weightKey
        if (!valueKey || !weightKey) return undefined
        return generateWeightedAverageFromParams(params, valueKey as never, weightKey as never)
      },
    }
  }, [extraAggFuncs])

  const { handleSelectionChanged } = useSelectedVariantHandlers()

  const handleSelectionChangedLocal = useCallback(
    (event: SelectionChangedEvent) => {
      if (rowSelection) handleSelectionChanged(event)
      if (onSelectionChanged) {
        onSelectionChanged(event)
      }
    },
    [handleSelectionChanged, onSelectionChanged, rowSelection],
  )

  const onGridPreDestroyed = useCallback(
    (params: GridPreDestroyedEvent<T>) => {
      // If the grid unmounts mid-inspection, the live state reflects the
      // transient splice/visibility — skip persistence so we don't poison the
      // saved state for the next mount.
      if (inspectionActiveRef.current || disableStatePersistence) {
        setGridApi(null)
        return
      }
      const { state } = params
      const sanitizedState = stripTransientGridState(state) ?? state
      saveState(sanitizedState)
      // Clear grid API from registry and local state when destroyed
      setGridApi(null)
    },
    [saveState, setGridApi, disableStatePersistence],
  )

  // Sort is restored on mount by handleGridReady + initialState; no effect re-applies it (that
  // fought the user on repeat/cleared clicks). Grid-state is the single source of truth.

  const [autoPagination] = useState(() =>
    createAutoPagination((api: GridApi) => measureAutoPageSize(containerRef.current, api)),
  )

  // Auto page size is retried via debounced model updates — firstDataRendered can fire
  // mid-layout while the viewport still has no height.
  const debouncedAutoPageSize = useMemo(
    () =>
      // eslint-disable-next-line react-hooks/refs -- debounce body runs async (not during render); the ref read is safe.
      debounce((api: GridApi) => {
        if (api.isDestroyed()) return
        autoPagination.remeasure(api)
      }, 150),
    [autoPagination],
  )
  useEffect(() => () => debouncedAutoPageSize.cancel(), [debouncedAutoPageSize])

  // Apply page size when it changes
  useEffect(() => {
    const gridApi = getGridApi()
    if (!gridApi || !showPagination) return

    // Guard: setGridOption resets current page to 0 even when the value is unchanged.
    // Skip calls where the grid already has the correct setting to preserve the page
    // restored by handleFirstDataRendered on navigation return.
    if (selectedPageSize === "auto") {
      autoPagination.resetPageSize(gridApi)
    } else if (gridApi.paginationGetPageSize() !== selectedPageSize) {
      gridApi.setGridOption("paginationPageSize", selectedPageSize)
    }
  }, [getGridApi, selectedPageSize, showPagination, autoPagination])

  const [exportConfig] = useExportConfig()
  const openConfigDialog = useConfigureExportDialogStore((state) => state.openDialog)
  const excelStyles = useMemo(
    () => excelStylesFor(exportConfig.numberOfDecimals),
    [exportConfig.numberOfDecimals],
  )

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams<T>): (DefaultMenuItem | MenuItemDef<T>)[] => {
      const defaultItems =
        (params.defaultItems?.filter(
          (item) =>
            !["copyWithGroupHeaders", "copyWithHeaders", "columnChooser", "resetColumns"].includes(
              item,
            ),
        ) as (DefaultMenuItem | MenuItemDef<T>)[]) ?? []
      const withExportConfig = defaultItems.map((item) =>
        item === "export"
          ? {
              name: "Export",
              subMenu: [
                "csvExport",
                "excelExport",
                {
                  name: "Configure exports…",
                  icon: getGearIconElement(),
                  action: openConfigDialog,
                },
              ],
            }
          : item,
      ) as (DefaultMenuItem | MenuItemDef<T>)[]
      const customItems = getContextMenuItemsProp?.(params) ?? []
      return [...customItems, ...withExportConfig]
    },
    [getContextMenuItemsProp, openConfigDialog],
  )

  // Always filter out columnChooser and resetColumns, then call custom handler if provided
  const wrappedGetMainMenuItems = useCallback(
    (params: GetMainMenuItemsParams<T>): (DefaultMenuItem | MenuItemDef<T>)[] => {
      const filteredDefaultItems = params.defaultItems.filter(
        (item) => !["columnChooser", "resetColumns"].includes(item),
      )
      if (getMainMenuItems) {
        return getMainMenuItems({ ...params, defaultItems: filteredDefaultItems })
      }
      return filteredDefaultItems as (DefaultMenuItem | MenuItemDef<T>)[]
    },
    [getMainMenuItems],
  )

  const sortChangedFn = useCallback(
    (params: SortChangedEvent) => {
      // Sort is persisted via gridState (onStateUpdated → saveState), including the cleared
      // (empty) sort, so nothing is persisted here. Handle the cleared case too: reset
      // pagination and forward the event to any consumer. "api" sorts come from re-applying saved
      // state (view restore, late custom-field columns), so they keep the page.
      if (showPagination && params.source !== "api") {
        autoPagination.cancelPendingRestore()
        params.api.paginationGoToFirstPage()
      }
      if (onSortChanged) {
        onSortChanged(params)
      }
    },
    [onSortChanged, showPagination, autoPagination],
  )

  const noRowsOverlayParams = useMemo(
    () => ({ noRowsMessage: emptyMessage ?? "No data" }),
    [emptyMessage],
  )

  const getRowId = useMemo<GetRowIdFunc>(() => {
    return (params: GetRowIdParams) => params.data?.[rowIdField]?.toString() ?? null
  }, [rowIdField])

  const defaultGroupAggFiltering = useMemo<boolean | IsRowFilterable>(() => {
    return (params: any) => params.node.level === 0 // Only apply to top level rows
  }, [])

  // AG Grid 36 regression: when `groupAggFiltering` is set, filters on columns
  // with an active aggFunc are classified as "aggregate filters" and applied only
  // by the enterprise FilterAggregatesStage — which no longer visits rows on
  // flat (ungrouped) grids, so those filters silently stop filtering rows.
  // Only enable groupAggFiltering while row grouping is actually active;
  // grouped grids filter correctly on v36.
  const [hasActiveGrouping, setHasActiveGrouping] = useState(false)
  const handleColumnRowGroupChanged = useCallback((event: ColumnRowGroupChangedEvent<T>) => {
    setHasActiveGrouping(event.api.getRowGroupColumns().length > 0)
  }, [])

  const paginationNumberFormatter = useCallback(
    (params: PaginationNumberFormatterParams) => {
      // This accounts for the grand total row being counted as a row in the pagination.
      if (params.value && params.value > 1 && grandTotalRow) {
        const finalValue = params.value - 1
        return finalValue.toLocaleString()
      }
      return params.value.toLocaleString()
    },
    [grandTotalRow],
  )

  const finalDefaultColumnDef = useMemo<ColDef>(() => {
    const baseDef = defaultColumnDef ?? {
      suppressHeaderMenuButton: true,
      sortable: true,
      minWidth: 100,
    }

    // Strip flex when saved column sizing exists - let saved widths take effect
    const hasSavedColumnSizing =
      !disableColumnSizingPersistence &&
      (initialState?.columnSizing?.columnSizingModel?.length ?? 0) > 0
    if (hasSavedColumnSizing && (baseDef.flex !== undefined || baseDef.initialFlex !== undefined)) {
      return Object.fromEntries(
        Object.entries(baseDef).filter(([key]) => key !== "flex" && key !== "initialFlex"),
      ) as ColDef
    }

    return baseDef
  }, [defaultColumnDef, initialState])

  const handleFirstDataRendered = useCallback(() => {
    if (!showPagination) return
    const api = getGridApi()
    if (!api) return
    autoPagination.restoreSavedPage(
      api,
      initialState?.pagination?.page ?? 0,
      selectedPageSize === "auto",
    )
  }, [getGridApi, initialState, showPagination, selectedPageSize, autoPagination])

  const totalsRowRepairAttemptsRef = useRef(0)
  const repairPinnedTotalsRow = useMemo(
    () =>
      // eslint-disable-next-line react-hooks/refs -- debounce body runs async (not during render); the ref reads are safe.
      debounce((api: GridApi<T>) => {
        if (api.isDestroyed() || api.getDisplayedRowCount() === 0) return
        const pinnedCount = api.getPinnedBottomRowCount()
        const gridRoot = containerRef.current?.querySelector(".ag-root-wrapper")
        const hasDomRow = [
          ...(gridRoot?.querySelectorAll(".ag-grid-pinned-bottom-rows [row-id]") ?? []),
        ].some((el) => el.closest(".ag-root-wrapper") === gridRoot)
        if (pinnedCount > 0 && hasDomRow) {
          totalsRowRepairAttemptsRef.current = 0
          // The row can be drawn before its aggregates land (rows handed over
          // from cache at mount) and keeps the blank cells until forced.
          const pinnedRows = Array.from({ length: pinnedCount }, (_, i) =>
            api.getPinnedBottomRow(i),
          ).filter((node) => node != null)
          api.refreshCells({ rowNodes: pinnedRows, force: true, suppressFlash: true })
          return
        }
        if (totalsRowRepairAttemptsRef.current >= 5) return
        totalsRowRepairAttemptsRef.current++
        api.setGridOption("grandTotalRow", undefined)
        api.setGridOption("grandTotalRow", grandTotalRow)
        api.redrawRows()
      }, 300),
    [grandTotalRow],
  )
  useEffect(() => () => repairPinnedTotalsRow.cancel(), [repairPinnedTotalsRow])
  const handleModelUpdated = useCallback(
    (event: ModelUpdatedEvent<T>) => {
      if (showPagination && selectedPageSize === "auto") debouncedAutoPageSize(event.api)
      if (grandTotalRow === "pinnedBottom") repairPinnedTotalsRow(event.api)
    },
    [showPagination, selectedPageSize, debouncedAutoPageSize, grandTotalRow, repairPinnedTotalsRow],
  )

  // Saved column widths can underfill the grid (saved on a narrower viewport, or the side
  // panel toggled away). Stretch proportionally to fill the dead space — but never shrink,
  // so wide tables keep their horizontal scroll.
  const stretchColumnsIfUnderfilled = useCallback((api: GridApi<T>) => {
    const gridWidth = containerRef.current?.querySelector(".ag-root")?.clientWidth ?? 0
    if (gridWidth <= 0) return
    const totalWidth = api
      .getAllDisplayedColumns()
      .reduce((sum, col) => sum + col.getActualWidth(), 0)
    if (totalWidth < gridWidth) api.sizeColumnsToFit()
  }, [])

  const handleGridReady = useCallback(
    (event: GridReadyEvent<T, any>) => {
      setGridApi(event.api, initialColIds)
      setHasActiveGrouping(event.api.getRowGroupColumns().length > 0)
      restoreAggregation(event.api, initialState)
      onGridReady?.(event)

      if (disableColumnSizingPersistence) {
        event.api.sizeColumnsToFit()
      } else {
        stretchColumnsIfUnderfilled(event.api)
      }

      const currentState = getCurrentState()

      const restoredFilterModel = event.api.getFilterModel() ?? {}
      const validFilterModel = dropStaleFilterModelEntries(restoredFilterModel, (colId) => {
        const column = event.api.getColumn(colId)
        return column ? (column.getColDef().filter ?? false) : undefined
      })
      if (Object.keys(validFilterModel).length !== Object.keys(restoredFilterModel).length) {
        event.api.setFilterModel(validFilterModel)
      }

      // Apply default filter model if no filter is currently set
      if (defaultFilterModel && Object.keys(defaultFilterModel).length > 0) {
        const currentFilterModel = event.api.getFilterModel()
        if (!currentFilterModel || Object.keys(currentFilterModel).length === 0) {
          event.api.setFilterModel(defaultFilterModel)
        }
      }

      // if the grid already has sort from gridState, don't override sort
      // but still ensure selection column is pinned
      const sortModelLength = currentState?.sort?.sortModel?.length
      if (sortModelLength && sortModelLength > 0) {
        return
      }

      if (initialSortColumn && initialSortColumn.direction) {
        const newState: GridState = {
          ...(currentState ?? initialState ?? {}),
          sort: {
            sortModel: [
              {
                colId: initialSortColumn.name?.toString() ?? "",
                sort: initialSortColumn.direction as "asc" | "desc",
              },
            ],
          },
        }
        // Its pagination predates the auto page size and would pin the grid at 100 rows.
        applyState(newState, { keepPagination: true })
      }
    },
    [
      onGridReady,
      initialState,
      restoreAggregation,
      initialSortColumn,
      applyState,
      setGridApi,
      initialColIds,
      getCurrentState,
      defaultFilterModel,
      disableColumnSizingPersistence,
      stretchColumnsIfUnderfilled,
    ],
  )

  // Default pins become initial pins, so a column definition update keeps the user's pin.
  const gridAutoGroupColumnDef = useMemo(
    () => autoGroupColumnDef && pinnedToInitial(autoGroupColumnDef),
    [autoGroupColumnDef],
  )
  // With inspection on, every leaf column also composes inspection styling onto its
  // `cellStyle` — not just those with `context.relationalFormula`: operand columns (e.g.
  // `currentStock` feeding `projectedStockStart`) have no formula of their own but
  // still render green/red/orange when their parent column is inspected.
  const gridColumnDefs = useMemo(
    () =>
      mapColDefLeaves(columnDefs, (def) => {
        const pinnedDef = pinnedToInitial(def)
        const colId = def.colId ?? def.field
        if (!inspectionEnabled || !colId) return pinnedDef
        return {
          ...pinnedDef,
          cellStyle: (params: CellClassParams<T>) =>
            composeCellStyle(
              def.cellStyle,
              getInspectionCellStyle(params, colId, formulasByColId),
              params,
            ),
        }
      }),
    [columnDefs, inspectionEnabled, formulasByColId],
  )

  // ── Inspection: merge `inspectedCell` into AG Grid `context` so cellStyle
  // callbacks can read it without closing over component state.
  const mergedContext = useMemo(
    () => (inspectionEnabled ? { ...(context ?? {}), inspectedCell } : context),
    [context, inspectedCell, inspectionEnabled],
  )

  const excelExportParams = useMemo(() => {
    const custom = gridOptions?.defaultExcelExportParams
    return {
      ...custom,
      processCellCallback: makeProcessExportCell("excel", custom?.processCellCallback),
    }
  }, [gridOptions?.defaultExcelExportParams])

  const csvExportParams = useMemo(() => {
    const custom = gridOptions?.defaultCsvExportParams
    return {
      ...custom,
      processCellCallback: makeProcessExportCell("csv", custom?.processCellCallback),
    }
  }, [gridOptions?.defaultCsvExportParams])

  // ── Inspection: derive a colId → headerName map for FormulaBar chip labels.
  const columnLabelsByColId = useMemo(() => {
    const labels: Record<string, string> = {}
    const walk = (defs: (ColDef<T> | ColGroupDef<T>)[]) => {
      for (const def of defs) {
        if (isColGroupDef(def)) {
          walk(def.children)
          continue
        }
        const colId = def.colId ?? def.field
        if (colId && def.headerName) labels[colId] = def.headerName
      }
    }
    walk(columnDefs)
    return labels
  }, [columnDefs])

  // ── Inspection: wrap any consumer-supplied `gridOptions.onCellClicked` so
  // the underlying handler still fires while we drive `inspectedCell`.
  const handleCellClicked = useCallback(
    (event: CellClickedEvent<T>) => {
      if (inspectionEnabled) {
        const colId = event.colDef.colId ?? event.colDef.field ?? ""
        const rowId = event.node.id ?? null
        if (event.node.group || !rowId) {
          setInspectedCell(null)
        } else if (formulasByColId.has(colId)) {
          setInspectedCell((prev) =>
            prev?.rowId === rowId && prev?.colId === colId ? null : { rowId, colId },
          )
        } else {
          // Click on a non-inspectable cell clears any active inspection.
          setInspectedCell(null)
        }
      }
      gridOptions?.onCellClicked?.(event)
    },
    [inspectionEnabled, formulasByColId, gridOptions],
  )

  const mergedGridOptions = useMemo<GridOptions<T> | undefined>(() => {
    if (!inspectionEnabled) return gridOptions
    return { ...(gridOptions ?? {}), onCellClicked: handleCellClicked }
  }, [gridOptions, handleCellClicked, inspectionEnabled])

  // ── Inspection: apply column splice on enter, restore baseline on exit.
  // `inspectionActiveRef` brackets the apply/restore so `debouncedSaveState`
  // discards the onStateUpdated events triggered by our own column ops.
  useEffect(() => {
    if (!inspectionEnabled) return
    const api = gridRef.current?.api
    if (!api) return

    if (inspectedCell) {
      const formula = formulasByColId.get(inspectedCell.colId)
      if (!formula) return

      // Capture baseline once per inspection session — used to restore on exit.
      if (!baselineOrderRef.current) {
        const currentColumnState = api.getColumnState()
        baselineOrderRef.current = currentColumnState.map((c) => c.colId)
        baselineHiddenRef.current = new Set(
          currentColumnState.filter((c) => c.hide).map((c) => c.colId),
        )
      }

      const newState = deriveInspectionColumnState(
        formula,
        inspectedCell.colId,
        baselineOrderRef.current,
        baselineHiddenRef.current ?? new Set<string>(),
      )
      if (!newState) return

      inspectionActiveRef.current = true
      api.applyColumnState({ state: newState, applyOrder: true })
      api.refreshCells({ force: true })
    } else if (baselineOrderRef.current) {
      const baseline = baselineOrderRef.current
      const hidden = baselineHiddenRef.current ?? new Set<string>()
      api.applyColumnState({
        state: baseline.map((colId) => ({ colId, hide: hidden.has(colId) })),
        applyOrder: true,
      })
      api.refreshCells({ force: true })
      baselineOrderRef.current = null
      baselineHiddenRef.current = null
      // Defer clearing the flag past AG Grid's onStateUpdated fan-out so the
      // restore-driven save event is also discarded.
      requestAnimationFrame(() => {
        inspectionActiveRef.current = false
      })
    }
  }, [inspectedCell, inspectionEnabled, formulasByColId, gridRef])

  const dismissInspection = useCallback(() => setInspectedCell(null), [])
  useDismissOnOutsideClick({
    isActive: !!inspectedCell,
    containerRef,
    onDismiss: dismissInspection,
  })

  const handleGridSizeChanged = useCallback(
    (event: GridSizeChangedEvent<T>) => {
      if (disableColumnSizingPersistence) {
        event.api.sizeColumnsToFit()
      } else {
        stretchColumnsIfUnderfilled(event.api)
      }
      if (showPagination && selectedPageSize === "auto") {
        autoPagination.remeasure(event.api)
      }
    },
    [
      disableColumnSizingPersistence,
      showPagination,
      selectedPageSize,
      autoPagination,
      stretchColumnsIfUnderfilled,
    ],
  )

  return (
    <div
      ref={containerRef}
      className={`w-full h-full flex flex-col gap-3`}
      data-test-id={`data-table-${id}`}
      data-table-compact={compact ? "true" : undefined}
    >
      <Card
        className={cn(
          !autoHeight && "flex-1 min-h-0",
          cardFooter && "flex flex-col",
          cardClassName,
        )}
        style={{
          width: "100%",
          height: autoHeight ? "100%" : undefined,
          minHeight: autoHeight ? undefined : `${minHeight}px`,
        }}
      >
        <div
          className={cn("w-full p-0 rounded-none", cardFooter ? "flex-1 min-h-0" : "h-full")}
          style={
            {
              backgroundColor:
                resolvedTheme === "light"
                  ? getTailwindValue("--color-gray-100")
                  : getTailwindValue("--color-gray-800"),
              ...(maxDetailRowHeight
                ? { "--detail-row-max-height": `${maxDetailRowHeight}px` }
                : {}),
            } as React.CSSProperties
          }
        >
          <AgGridReact<T>
            key={id}
            columnDefs={gridColumnDefs}
            defaultColDef={finalDefaultColumnDef}
            ref={gridRef}
            rowData={rowData}
            pinnedTopRowData={pinnedTopRowData}
            quickFilterText={quickFilterText}
            gridOptions={mergedGridOptions}
            rowSelection={rowSelection}
            groupLockGroupColumns={-1} // Lock all columns so that it is only controlled by AggregationSelector and us.
            grandTotalRow={grandTotalRow}
            pagination={showPagination}
            paginationPageSize={selectedPageSize === "auto" ? undefined : selectedPageSize}
            suppressPaginationPanel={true}
            paginationPageSizeSelector={false}
            paginationNumberFormatter={paginationNumberFormatter}
            // AG Grid's async tooltip positioning can fire after the grid
            // unmounts; anchoring to document.body keeps the popup parent valid
            // so getBoundingClientRect doesn't dereference null on route nav.
            popupParent={document.body}
            tooltipInteraction
            tooltipShowDelay={750}
            alwaysShowVerticalScroll={true}
            scrollbarWidth={getGridScrollbarWidth()}
            enableGroupEdit
            undoRedoCellEditing
            getRowId={gridOptions?.getRowId ?? getRowId}
            selectionColumnDef={selectionColumnDef}
            // Locale-aware string sort (alphabetical, not case-sensitive ASCII).
            accentedSort={true}
            suppressAggFuncInHeader={true}
            valueCache={true}
            aggFuncs={aggFuncs}
            theme={customTheme}
            rowBuffer={gridOptions?.rowBuffer ?? 25}
            // suppressColumnVirtualisation // Horizontal scroll lags if this is not set...
            enableCellTextSelection={enableCellTextSelection}
            noRowsOverlayComponent={NoRowsOverlay}
            noRowsOverlayComponentParams={noRowsOverlayParams}
            onSelectionChanged={handleSelectionChangedLocal}
            autoGroupColumnDef={gridAutoGroupColumnDef}
            initialState={initialState}
            onGridPreDestroyed={onGridPreDestroyed}
            onStateUpdated={debouncedSaveState}
            groupAggFiltering={
              hasActiveGrouping
                ? groupAggFiltering === undefined
                  ? defaultGroupAggFiltering
                  : groupAggFiltering
                : undefined
            }
            onColumnRowGroupChanged={handleColumnRowGroupChanged}
            suppressAggFilteredOnly={false}
            onSortChanged={sortChangedFn}
            getMainMenuItems={wrappedGetMainMenuItems}
            getContextMenuItems={getContextMenuItems}
            loading={loading}
            loadingOverlayComponent={TableLoadingOverlay}
            sideBar={sideBar}
            groupHideParentOfSingleChild={groupHideParentOfSingleChild}
            masterDetail={masterDetail}
            detailCellRendererParams={detailCellRendererParams}
            detailCellRenderer={detailCellRenderer}
            keepDetailRows={keepDetailRows}
            detailRowAutoHeight={detailRowAutoHeight} // Setting to true make the detail row header not sticky.
            detailRowHeight={500}
            domLayout={autoHeight ? "autoHeight" : "normal"}
            enableRowPinning={enableRowPinning}
            rowDragMultiRow={rowDragMultiRow}
            onRowDragEnd={onRowDragEnd}
            isRowPinned={isRowPinned}
            groupDisplayType={groupDisplayType}
            onCellValueChanged={onCellValueChanged}
            context={mergedContext}
            onCellClicked={onCellClicked}
            maintainColumnOrder={true}
            stopEditingWhenCellsLoseFocus={true}
            onFirstDataRendered={handleFirstDataRendered}
            onModelUpdated={handleModelUpdated}
            onGridReady={handleGridReady}
            onGridSizeChanged={handleGridSizeChanged}
            onDragStopped={onDragStopped}
            onNewColumnsLoaded={onNewColumnsLoaded}
            onFilterChanged={onFilterChanged}
            excelStyles={excelStyles}
            defaultExcelExportParams={excelExportParams}
            defaultCsvExportParams={csvExportParams}
          />
        </div>
        {cardFooter}
      </Card>
      {showPagination && (
        <div className={cn(footerClassName)}>
          <CustomPagination
            tableId={id}
            selectedPageSize={selectedPageSize}
            onPageSizeChange={setSelectedPageSize}
          />
        </div>
      )}
      {inspectionEnabled &&
        inspectedCell &&
        // eslint-disable-next-line react-hooks/refs
        (() => {
          const inspectedRow = rowData?.find(
            (r) => (r as Record<string, unknown>)[rowIdField]?.toString() === inspectedCell.rowId,
          )
          const formula = formulasByColId.get(inspectedCell.colId)
          if (!inspectedRow || !formula) return null
          const rowRecord = inspectedRow as Record<string, unknown>
          const noteFn = noteFnsByColId.get(inspectedCell.colId)
          const params = formulaParams ?? {}
          const resolveCol = (colId: string): number => {
            const def = leafDefsByColId.get(colId)

            // 1. Explicit override
            const valueFn = def && relationalFormulaContext(def)?.relationalFormulaValue
            if (valueFn) {
              const v = valueFn({ row: rowRecord, params })
              return Number.isFinite(v) ? v : 0
            }

            // 2. Auto-adapt valueGetter
            if (typeof def?.valueGetter === "function") {
              try {
                const api = gridRef.current?.api
                const rowNode = api?.getRowNode(inspectedCell.rowId)
                const result = def.valueGetter({
                  data: rowRecord as T,
                  node: rowNode ?? ({ group: false, data: rowRecord } as IRowNode<T>),
                  context: mergedContext,
                  colDef: def,
                  api,
                  column: api?.getColumn(colId),
                  getValue: (field: string) => (rowRecord as Record<string, unknown>)[field],
                } as any)
                if (typeof result === "number") return Number.isFinite(result) ? result : 0
                // Common object shape (e.g. PO Table's IncomingStock returns
                // { value, incomingOrders, ... } for richer cell rendering).
                if (
                  result &&
                  typeof result === "object" &&
                  "value" in result &&
                  typeof (result as { value: unknown }).value === "number"
                ) {
                  return (result as { value: number }).value
                }
                return Number(result ?? 0)
              } catch {
                // Fall through to default — likely a getter using fields we
                // didn't synthesize (e.g. an exotic `api.forEach...` call).
              }
            }

            // 3. Default — prefer `field` when set (covers the common case where
            // colId and field differ, e.g. nested-path or renamed display id).
            const fieldKey =
              typeof def?.field === "string" && def.field.length > 0 ? def.field : colId
            return Number(rowRecord[fieldKey] ?? 0)
          }
          const cellValue = resolveCol(inspectedCell.colId)
          const note =
            noteFn?.({
              row: rowRecord,
              params,
              result: cellValue,
            }) ?? undefined
          return (
            <FormulaBar
              formula={formula}
              row={rowRecord}
              columnLabels={columnLabelsByColId}
              params={params}
              paramLabels={formulaParamLabels ?? {}}
              note={note || undefined}
              cellValue={cellValue}
              resolveCol={resolveCol}
              onClear={() => setInspectedCell(null)}
            />
          )
        })()}
    </div>
  )
}
